/**
 * EventEmitter compatible navigateur
 * Implémentation simplifiée pour remplacer le module Node.js 'events'
 */

export class EventEmitter {
  private events: Map<string, Function[]> = new Map();

  /**
   * Ajouter un écouteur d'événement
   */
  on(event: string, listener: Function): this {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event)!.push(listener);
    return this;
  }

  /**
   * Ajouter un écouteur d'événement (alias de on)
   */
  addListener(event: string, listener: Function): this {
    return this.on(event, listener);
  }

  /**
   * Supprimer un écouteur d'événement
   */
  off(event: string, listener: Function): this {
    if (!this.events.has(event)) {
      return this;
    }
    
    const listeners = this.events.get(event)!;
    const index = listeners.indexOf(listener);
    if (index !== -1) {
      listeners.splice(index, 1);
    }
    
    return this;
  }

  /**
   * Supprimer un écouteur d'événement (alias de off)
   */
  removeListener(event: string, listener: Function): this {
    return this.off(event, listener);
  }

  /**
   * Supprimer tous les écouteurs d'un événement
   */
  removeAllListeners(event?: string): this {
    if (event) {
      this.events.delete(event);
    } else {
      this.events.clear();
    }
    return this;
  }

  /**
   * Émettre un événement
   */
  emit(event: string, ...args: any[]): boolean {
    if (!this.events.has(event)) {
      return false;
    }
    
    const listeners = this.events.get(event)!;
    listeners.forEach(listener => {
      try {
        listener(...args);
      } catch (error) {
        console.error(`Erreur dans l'écouteur d'événement ${event}:`, error);
      }
    });
    
    return listeners.length > 0;
  }

  /**
   * Obtenir le nombre d'écouteurs pour un événement
   */
  listenerCount(event: string): number {
    return this.events.get(event)?.length || 0;
  }

  /**
   * Obtenir les écouteurs d'un événement
   */
  listeners(event: string): Function[] {
    return this.events.get(event) || [];
  }

  /**
   * Obtenir tous les noms d'événements
   */
  eventNames(): string[] {
    return Array.from(this.events.keys());
  }

  /**
   * Vérifier si un événement a des écouteurs
   */
  hasListeners(event: string): boolean {
    return this.listenerCount(event) > 0;
  }

  /**
   * Supprimer tous les écouteurs et nettoyer
   */
  destroy(): void {
    this.removeAllListeners();
  }
}
